import { ReactNode } from "react";
import { ClerkProvider } from "@clerk/clerk-react";
import {
  deDE,
  enUS,
  esES,
  frFR,
  itIT,
  ptPT,
} from "@clerk/localizations";
import { useLingui } from "@lingui/react";
import { type Locale } from "@/lib/i18n";

type ClerkLocalization = typeof enUS;

// Replace Clerk's default "Sign in to {{applicationName}}" / "Continue to {{applicationName}}"
// strings so the app name doesn't appear twice next to our own wordmark on the sign-in card.
function withShortTitle(
  base: ClerkLocalization,
  title: string,
  subtitle: string,
): ClerkLocalization {
  return {
    ...base,
    signIn: {
      ...base.signIn,
      start: {
        ...base.signIn?.start,
        title,
        subtitle,
      },
    },
  };
}

const CLERK_LOCALIZATIONS: Record<Locale, ClerkLocalization> = {
  en: withShortTitle(enUS, "Sign in", "Welcome back"),
  de: withShortTitle(deDE, "Anmelden", "Willkommen zurück"),
  es: withShortTitle(esES, "Iniciar sesión", "Bienvenido de nuevo"),
  fr: withShortTitle(frFR, "Se connecter", "Bon retour"),
  it: withShortTitle(itIT, "Accedi", "Bentornato"),
  pt: withShortTitle(ptPT, "Entrar", "Bem-vindo de volta"),
};

interface LocalizedClerkProviderProps {
  publishableKey: string;
  children: ReactNode;
}

export function LocalizedClerkProvider({
  publishableKey,
  children,
}: LocalizedClerkProviderProps) {
  const { i18n } = useLingui();
  const locale = (i18n.locale in CLERK_LOCALIZATIONS
    ? i18n.locale
    : "en") as Locale;

  return (
    <ClerkProvider
      key={locale}
      publishableKey={publishableKey}
      signInUrl="/sign-in"
      signInFallbackRedirectUrl="/"
      localization={CLERK_LOCALIZATIONS[locale]}
    >
      {children}
    </ClerkProvider>
  );
}
